import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateSingleDevicePageSelectedRasterId, updateSingleDevicePageSelectedRastersDictionary } from 'redux/actions';
import { reactTableSortOnClickInFuncComp } from 'common/utils/helpers';
import { createFileName, polarizationTranslator } from '../HeatmapViewAndExportModal/utils/helpers';
import { LOADING_CIRCLE, Status } from 'common/utils/constants';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import moment from 'moment-timezone';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import HeatmapLibraryTableIdCell from './HeatmapLibraryTableIdCell/HeatmapLibraryTableIdCell';
import HeatmapLibraryTableRow from './HeatmapLibraryTableRow/HeatmapLibraryTableRow';
import './HeatmapLibraryTable.css';

const HeatmapLibraryTable = (props) => {
  const dispatch = useDispatch();
  const [sorted, setSorted] = useState([]);
  const deviceHeatmapHistoryDictionary = useSelector(
    (store) => store.data.heatmapHistoryDictionary?.[props.donorDevice] ?? {}
  );
  const getRasterRecordsForDeviceStatusName = `GET-RasterRecordsForDevice-${props.donorDevice}`;
  const getRasterRecordsForDeviceStatus = useSelector((store) => store.statuses[getRasterRecordsForDeviceStatusName]);
  const rowIdx = useRef(0);

  useEffect(() => {
    if (Object.keys(deviceHeatmapHistoryDictionary)?.length > 0) {
      let selectionDictionary = {};
      let rasterIds = Object.keys(deviceHeatmapHistoryDictionary);
      rasterIds.forEach((rasterId) => {
        selectionDictionary[rasterId] = false;
      });
      dispatch(updateSingleDevicePageSelectedRastersDictionary(selectionDictionary));
    }
  }, [deviceHeatmapHistoryDictionary]);

  const columns = [
    {
      id: 'source_type',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        let rasterColHeader = 'SOURCE';

        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv">
            {/* <HeatmapLibraryTableHeaderCheckbox
              donorDevice={props.donorDevice}
            /> */}
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={rasterColHeader}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'source_type')}
            />
            <div id="HeatmapLibraryTable_ResizerSliderIcon" className="HeatmapLibraryTable_ResizerSliderIcon">
              <div
                id="HeatmapLibraryTable_ResizerSliderIcon_Bar"
                className="HeatmapLibraryTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      className: 'HeatmapLibraryTable_IdColumn',
      accessor: (record) => {
        return record;
      },
      Cell: (row) => {
        let sourceText = row.value?.source ?? 'Unknown';

        return (
          <HeatmapLibraryTableIdCell
            rasterId={row.value?.rasterId}
            sourceText={sourceText}
            deviceHeatmapHistoryDictionary={deviceHeatmapHistoryDictionary}
            donorDevice={props.donorDevice}
          />
        );
      },
      sortMethod: (a, b) => {
        let a1 = a?.source ?? 'Unknown';
        let b1 = b?.source ?? 'Unknown';
        return a1.toLowerCase() > b1.toLowerCase() ? 1 : -1;
      }
    },
    {
      id: 'date_time',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'DATE/TIME'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'date_time')}
            />
            <div id="HeatmapLibraryTable_ResizerSliderIcon" className="HeatmapLibraryTable_ResizerSliderIcon">
              <div
                id="HeatmapLibraryTable_ResizerSliderIcon_Bar"
                className="HeatmapLibraryTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        return record?.ts?.['$date'] ? record.ts.$date : record.ts;
      },
      Cell: (row) => {
        let displayString = moment(row.value)?.format('MMM D YYYY, HH:mm');
        return (
          <div id="DeviceTable_cellWrapper" className="DeviceTable_cellWrapper" data-tip={displayString}>
            {displayString}
          </div>
        );
      }
    },
    {
      id: 'polarization_type',
      minWidth: 60,
      resizable: true,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'POLARIZATION'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'polarization_type')}
            />
            <div id="HeatmapLibraryTable_ResizerSliderIcon" className="HeatmapLibraryTable_ResizerSliderIcon">
              <div
                id="HeatmapLibraryTable_ResizerSliderIcon_Bar"
                className="HeatmapLibraryTable_ResizerSliderIcon_Bar"
              ></div>
            </div>
          </div>
        );
      },
      accessor: (record) => {
        let polarity = record?.configuration?.polarization;
        // If polarization is not defined in the configuration, determine it based on acq_mode
        if (!polarity) {
          // Check if acq_mode includes 'on-V' (vertical), then set polarity to VPOL
          if (record?.meta?.acq_mode.includes('on-V')) {
            polarity = 'VPOL'; 
          } else {
            polarity = 'HPOL';
          }
        }
      
        // Use a translation function to map the polarity to a more meaningful representation
        let polarization = polarizationTranslator(polarity);
      
        // Return the translated polarization value
        return polarization;
      },
      
      Cell: (row) => {
        return (
          <div id="DeviceTable_cellWrapper" className="DeviceTable_cellWrapper" data-tip={row.value}>
            {row.value}
          </div>
        );
      }
    },
    {
      id: 'file_name',
      minWidth: 150,
      resizable: false,
      Header: (row) => {
        return (
          <div id="DeviceTable_Header_OuterDiv" className="DeviceTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'FILE NAME'}
              reverse
              onClick={() => reactTableSortOnClickInFuncComp(sorted, setSorted, 'file_name')}
            />
          </div>
        );
      },
      accessor: (record) => {
        let fileName = createFileName(record);
        return fileName;
      },
      Cell: (row) => {
        return (
          <div id="DeviceTable_cellWrapper" className="DeviceTable_cellWrapper" data-tip={row.value}>
            {row.value}
          </div>
        );
      }
    }
  ];

  const handleOnSortedChange = (newSorted) => {
    setSorted(newSorted);
  };

  let pageSize = Object.keys(deviceHeatmapHistoryDictionary)?.length ?? 7;

  let overlay = null;
  if (
    getRasterRecordsForDeviceStatus === Status.FAILURE &&
    (deviceHeatmapHistoryDictionary == null || Object.keys(deviceHeatmapHistoryDictionary)?.length === 0)
  ) {
    overlay = (
      <div className="Global_Table_ErrorOverlay">
        <div className="Global_Table_Error_Div">
          <div className="Global_Table_ErrorOverlay_Title">Error </div>
          <div className="Global_Table_ErrorOverlay_Message">
            An unexpected problem occurred and this data is unavailable. Please try refreshing the page, and if the
            problem persists, contact us at
            <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
          </div>
        </div>
      </div>
    );
  } else if (getRasterRecordsForDeviceStatus === Status.PENDING) {
    overlay = LOADING_CIRCLE;
  } else if (
    getRasterRecordsForDeviceStatus === Status.NONE &&
    Object.keys(deviceHeatmapHistoryDictionary)?.length === 0
  ) {
    overlay = (
      <div id="HeatmapLibraryTable_NoData" className="HeatmapLibraryTable_NoData">
        There is no records for this devices.
      </div>
    );
  }

  return (
    <div id="HeatmapLibraryTable_Container" className="HeatmapLibraryTable_Container">
      <div id="HeatmapLibraryTable_Div" className="HeatmapLibraryTable_Div">
        <ScrollReactTable
          showPagination={false}
          noDataText={''}
          className={'-highlight Global_Table HeatmapLibraryTable'}
          resizable={true}
          sortable={false}
          pageSize={pageSize}
          data={Object.values(deviceHeatmapHistoryDictionary)}
          overlay={overlay}
          fetching={false}
          sorted={sorted}
          onSortedChange={(newSorted) => handleOnSortedChange(newSorted)}
          columns={columns}
          getTdProps={(state, rowInfo, column, instance) => {
            return {
              onClick: (e, handleOriginal) => {
                // e.preventDefault();
                if (rowInfo && rowInfo.index !== null) {
                  let record = rowInfo.original;
                  let rasterId = record.rasterId;
                  // let deviceId = record.deviceId;
                  rowIdx.current = rowInfo.index;
                  dispatch(updateSingleDevicePageSelectedRasterId(rasterId));
                }
                if (handleOriginal) {
                  handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                }
              }
            };
          }}
          getTrProps={(state, rowInfo) => {
            if (rowInfo) {
              let isMatchedIdx = rowInfo.index === rowIdx.current;
              return {
                style: {
                  background: isMatchedIdx ? '#f5f8fa' : 'white',
                  border: isMatchedIdx ? 'solid 1px #ffa471' : 'solid 1px #CBCBCB',
                  borderRadius: '2px'
                }
              };
            }
            return {};
          }}
          TrComponent={(props, index) => <HeatmapLibraryTableRow {...props} />}
          // TheadComponent={(props) => (
          //   <HeatmapLibraryTableHeader
          //     {...props}
          //     hasCheckbox={props.hasCheckbox}
          //   />
          // )}
        />
      </div>
    </div>
  );
};

export default React.memo(HeatmapLibraryTable);
